import { FC, useMemo } from 'react';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { PurchaseAgreement } from '@retail/purchase-agreements/types';
import { usePurchaseAgreementsTFunction } from '@retail/purchase-agreements/i18n';
import { Checkbox } from '@retail/components';

interface Props {
  agreements: PurchaseAgreement[];
  compareMode?: boolean;
  checkedAgreements: PurchaseAgreement[];
  handleCheckAgreement: (agreement: PurchaseAgreement) => void;
}

type GridColAgreement = GridColDef<PurchaseAgreement>;

const baseColumnProps: Partial<GridColAgreement> = {
  minWidth: 60,
  align: 'center',
  headerAlign: 'center',
  sortable: false,
  flex: 1,
};

export const PurchaseAgreementsTable: FC<Props> = ({
  agreements,
  compareMode = false,
  checkedAgreements,
  handleCheckAgreement,
}) => {
  const t = usePurchaseAgreementsTFunction();
  const columns: GridColAgreement[] = useMemo(() => {
    const checkboxColDef: GridColAgreement = {
      ...baseColumnProps,
      field: 'action',
      width: 60,
      flex: 0,
      headerName: t('purchaseAgreements.choose'),
      renderCell: ({ row }) => (
        <Checkbox
          labelplacement="top"
          checked={checkedAgreements.map((agreement) => agreement.id).includes(row.id)}
          onClick={() => handleCheckAgreement(row)}
        />
      ),
    };
    const nameColDef: GridColAgreement = {
      ...baseColumnProps,
      field: 'name',
      align: 'left',
      headerAlign: 'left',
      width: 140,
      headerName: t('purchaseAgreements.agreement.columns.agreementText'),
    };
    const sourceColDef: GridColAgreement = {
      ...baseColumnProps,
      field: 'source',
      headerName: t('purchaseAgreements.agreement.columns.source'),
    };
    const sourceAgreementIdColDef: GridColAgreement = {
      ...baseColumnProps,
      field: 'sourceAgreementId',
      headerName: t('purchaseAgreements.agreement.columns.sourceAgreementId'),
    };
    const idColDef: GridColAgreement = {
      ...baseColumnProps,
      field: 'id',
      headerName: t('purchaseAgreements.agreement.columns.ourId'),
    };

    const columnsToShow = compareMode
      ? [checkboxColDef, nameColDef]
      : [nameColDef, sourceColDef, sourceAgreementIdColDef, idColDef];

    return columnsToShow;
  }, [checkedAgreements, compareMode, handleCheckAgreement, t]);

  return (
    <DataGridPremium
      columns={columns}
      rows={agreements}
      disableColumnMenu
      disableRowSelectionOnClick
      hideFooter
    />
  );
};
